import { ArrowLeft, Minus, Plus, Trash2, Zap } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { formatINR } from "../lib/api";

const FREE_DELIVERY_ABOVE = 599;
const DELIVERY_FEE = 20;

export default function Cart() {
  const { items, subtotal, inc, dec, remove } = useCart();
  const navigate = useNavigate();

  const freeUnlocked = subtotal >= FREE_DELIVERY_ABOVE;
  const deliveryFee = freeUnlocked || subtotal === 0 ? 0 : DELIVERY_FEE;
  const total = subtotal + deliveryFee;
  const remaining = Math.max(0, FREE_DELIVERY_ABOVE - subtotal);
  const progress = Math.min(100, Math.round((subtotal / FREE_DELIVERY_ABOVE) * 100));

  return (
    <main className="mx-auto min-h-screen max-w-xl bg-[#F4F6F8] px-3 pb-44 pt-4">
      <header className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="cart-back"
          className="grid h-10 w-10 place-items-center rounded-full bg-white"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-extrabold text-[#111111]">Your Cart</h1>
      </header>

      {items.length === 0 ? (
        <section className="mt-12 text-center" data-testid="cart-empty">
          <p className="text-lg font-bold">Your cart is empty</p>
          <Link
            to="/"
            className="mt-4 inline-block rounded-lg bg-[#FF5200] px-4 py-3 font-bold text-white"
          >
            Start shopping
          </Link>
        </section>
      ) : (
        <>
          {/* Free delivery progress */}
          {freeUnlocked ? (
            <div
              className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3"
              data-testid="free-delivery-unlocked"
            >
              <p className="flex items-center gap-1.5 text-sm font-extrabold text-emerald-700">
                <Zap size={15} className="fill-emerald-600" /> 🎉 You unlocked FREE DELIVERY!
              </p>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-emerald-100">
                <div className="h-full w-full bg-emerald-500 transition-all duration-500" />
              </div>
            </div>
          ) : (
            <div
              className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-3"
              data-testid="free-delivery-progress"
            >
              <p className="flex items-center gap-1.5 text-sm font-bold text-orange-700">
                <Zap size={15} className="fill-orange-500" /> Add {formatINR(remaining)} more to get
                FREE DELIVERY
              </p>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-orange-100">
                <div
                  className="h-full rounded-full bg-[#FF5200] transition-all duration-500"
                  style={{ width: `${progress}%` }}
                  data-testid="free-delivery-bar"
                />
              </div>
            </div>
          )}

          <section className="mt-4 space-y-3">
            {items.map((item) => (
              <article key={item.line_id} className="flex items-center gap-3 rounded-xl bg-white p-3">
                <img src={item.image || "/product-placeholder.svg"} alt="" className="h-16 w-16 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold">{item.name}</p>
                  <p className="text-xs text-slate-500">{item.variant_label || ""}</p>
                  <p className="mt-1 font-bold">{formatINR(item.price)}</p>
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-slate-100 p-1">
                  <button onClick={() => dec(item.line_id)} data-testid={`cart-route-dec-${item.line_id}`} className="p-1.5">
                    <Minus size={15} />
                  </button>
                  <b className="min-w-4 text-center">{item.qty}</b>
                  <button onClick={() => inc(item.line_id)} data-testid={`cart-route-inc-${item.line_id}`} className="p-1.5">
                    <Plus size={15} />
                  </button>
                </div>
                <button onClick={() => remove(item.line_id)} data-testid={`cart-route-remove-${item.line_id}`} className="p-2 text-red-500">
                  <Trash2 size={15} />
                </button>
              </article>
            ))}
          </section>

          {/* Bill summary */}
          <section className="mt-4 rounded-xl bg-white p-4" data-testid="cart-bill-summary">
            <h2 className="text-sm font-extrabold text-slate-900">Bill details</h2>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span data-testid="bill-subtotal">{formatINR(subtotal)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Delivery fee</span>
                <span data-testid="bill-delivery">
                  {deliveryFee === 0 ? (
                    <span className="font-bold text-emerald-600">FREE</span>
                  ) : (
                    formatINR(deliveryFee)
                  )}
                </span>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-2 text-base font-extrabold text-slate-900">
                <span>Total Amount</span>
                <span data-testid="bill-total">{formatINR(total)}</span>
              </div>
            </div>
          </section>
        </>
      )}

      {items.length > 0 && (
        <footer className="fixed inset-x-0 bottom-20 z-[999] border-t border-slate-200 bg-white p-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)]">
          <div className="mx-auto flex max-w-xl items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-slate-500">Total</p>
              <p className="text-lg font-extrabold">{formatINR(total)}</p>
            </div>
            <button
              onClick={() => navigate("/checkout")}
              data-testid="cart-proceed-checkout"
              className="min-h-12 rounded-lg bg-[#FF5200] px-6 text-sm font-extrabold text-white transition-transform active:scale-95"
            >
              Proceed to Pay
            </button>
          </div>
        </footer>
      )}
    </main>
  );
}
